import React, { lazy, useEffect, useState } from 'react';
import { observer } from "mobx-react-lite";
import { sendSelectionMail } from "../../../../http/selectionApi";

import sharedStyles from '../UserModals.module.css';
import styles from './FormSelection.module.css';
import sharedInputStyles from '../../../adds/AddCreateForm/AddCreateForm.module.css';

import { reachGoal } from "../../../../utils/metrika/metrika";
import { trackEvent } from "../../../../utils/analytics/analytics";

const IMaskInput = lazy(() =>
    import("react-imask").then((m) => ({ default: m.IMaskInput }))
);

const FormSelection = observer(({ show, onHide }) => {
    const PhoneMask = "+0 (000) 000-00-00";

    const [phone, setPhone] = useState('');
    const [comment, setComment] = useState('');

    const [phoneError, setPhoneError] = useState('');
    const [consentError, setConsentError] = useState('');

    const [isConsentGiven, setIsConsentGiven] = useState(false);
    const [isSent, setIsSent] = useState(false);

    useEffect(() => {
        if (!show) return;

        trackEvent('selection_form_open', {
            source: 'main_or_site',
            meta: {
                form: 'selection_simple'
            }
        });
    }, [show]);

    const resetForm = () => {
        setPhone('');
        setComment('');
        setPhoneError('');
        setConsentError('');
        setIsConsentGiven(false);
        setIsSent(false);
    };

    const handleClose = () => {
        resetForm();
        onHide();
    };

    const sendForm = async () => {
        setPhoneError('');
        setConsentError('');

        let isValid = true;

        if (!phone || phone.length !== 18) {
            setPhoneError('Введите телефон полностью');
            isValid = false;
        }

        if (!isConsentGiven) {
            setConsentError('Нужно дать согласие на обработку данных');
            isValid = false;
        }

        if (!isValid) return;

        try {
            await sendSelectionMail({
                phone,
                comment,
            });

            trackEvent('request_sent', {
                source: 'selection_form',
                meta: {
                    form: 'selection_simple',
                    hasComment: Boolean(comment?.trim())
                }
            });

            reachGoal("sendSelectionMail");
            setIsSent(true);
        } catch (e) {
            console.error("Ошибка при отправке заявки:", e);
        }
    };

    return (
        <div
            className={`${sharedStyles.modal} ${show ? sharedStyles.active : ''}`}
            onClick={handleClose}
        >
            <div
                className={`${sharedStyles.modalContent} ${styles.selectionModalContent}`}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    className={sharedStyles.customCloseButton}
                    onClick={handleClose}
                    aria-label="Закрыть"
                >
                    &times;
                </button>

                <div className={styles.formShell}>
                    <div className={`${sharedStyles.bgCircle} ${sharedStyles.bgCircleBigCircleLeft}`}></div>
                    <div className={`${sharedStyles.bgCircle} ${sharedStyles.bgCircleCircleBottomRight}`}></div>
                    <div className={`${sharedStyles.bgCircle} ${sharedStyles.bgCircleCircleTopRight}`}></div>

                    {isSent ? (
                        <div className={styles.successContainer}>
                            <div className={styles.badge}>Заявка отправлена</div>

                            <h2 className={styles.successTitle}>
                                Мы получили ваш запрос
                            </h2>

                            <p className={styles.successText}>
                                Свяжемся с вами и предложим подходящие свободные варианты.
                            </p>

                            <button
                                type="button"
                                className={styles.primaryButton}
                                onClick={handleClose}
                            >
                                Закрыть
                            </button>
                        </div>
                    ) : (
                        <div className={styles.formContainer}>
                            <div className={styles.badge}>Бесплатный подбор жилья</div>

                            <h2 className={styles.formTitle}>Заявка на подбор</h2>

                            <p className={styles.formSubtitle}>
                                Оставьте телефон, а если хотите — коротко напишите, что для вас важно.
                            </p>

                            <div className={styles.inputGroup}>
                                <label className={styles.fieldLabel}>Введите ваш телефон</label>

                                <IMaskInput
                                    mask={PhoneMask}
                                    value={phone}
                                    placeholder="Ваш телефон"
                                    onAccept={(value) => setPhone(value)}
                                    className={`${styles.formInput} ${phoneError ? sharedInputStyles.errorInput : ''}`}
                                />

                                <div className={styles.errorPlaceholder}>
                                    {phoneError && <div className={styles.errorText}>{phoneError}</div>}
                                </div>
                            </div>

                            <div className={styles.inputGroup}>
                                <label className={styles.fieldLabel}>
                                    Пожелания <span className={styles.optional}>(необязательно)</span>
                                </label>

                                <textarea
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    placeholder="Например: рядом с парком, с парковкой, до 5000 ₽, на ближайшие даты"
                                    className={styles.formTextarea}
                                    rows={4}
                                />
                            </div>

                            <div className={styles.inputGroup}>
                                <label className={styles.checkboxLabelWrapper}>
                                    <input
                                        type="checkbox"
                                        className={styles.checkboxInput}
                                        checked={isConsentGiven}
                                        onChange={(e) => setIsConsentGiven(e.target.checked)}
                                    />

                                    <span className={styles.checkboxText}>
                                        Я даю{' '}
                                        <a
                                            rel="noreferrer noopener"
                                            target="_blank"
                                            href={process.env.REACT_APP_API_URL + 'policy/agreement_1.pdf'}
                                            className={styles.termsLink}
                                            aria-label="Открыть согласие на обработку персональных данных"
                                        >
                                            согласие
                                        </a>{' '}
                                        на обработку персональных данных в соответствии с{' '}
                                        <a
                                            rel="noreferrer noopener"
                                            target="_blank"
                                            href={process.env.REACT_APP_API_URL + 'policy/privacyPolicy.pdf'}
                                            className={styles.termsLink}
                                            aria-label="Открыть политику конфиденциальности"
                                        >
                                            политикой конфиденциальности
                                        </a>{' '}
                                        и подтверждаю ознакомление с{' '}
                                        <a
                                            rel="noreferrer noopener"
                                            target="_blank"
                                            href={process.env.REACT_APP_API_URL + 'policy/TermsOfUse.pdf'}
                                            className={styles.termsLink}
                                            aria-label="Открыть правила использования Arento"
                                        >
                                            правилами использования Arento
                                        </a>
                                    </span>
                                </label>

                                <div className={styles.errorPlaceholder}>
                                    {consentError && <div className={styles.errorText}>{consentError}</div>}
                                </div>
                            </div>

                            <div className={styles.actions}>
                                <button
                                    type="button"
                                    onClick={sendForm}
                                    className={styles.primaryButton}
                                >
                                    Получить варианты квартир
                                </button>

                                <div className={styles.helperText}>
                                    Уточним детали уже в сообщении или звонке
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

export default FormSelection;